import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (activeCat !== "All") params.category = activeCat;
    if (query.trim()) params.search = query.trim();
    const t = setTimeout(() => {
      api
        .get("/products", { params })
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [activeCat, query]);

  const allCats = useMemo(() => ["All", ...categories], [categories]);

  return (
    <div data-testid="products-page" className="min-h-screen bg-white pt-20">
      {/* header */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 pt-16 md:pt-24">
        <Reveal>
          <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">Catalogue</p>
          <h1 className="mt-4 font-heading text-5xl font-black uppercase leading-[0.9] tracking-tighter md:text-8xl">
            The Hardware
          </h1>
        </Reveal>

        <div className="mt-10 flex items-center gap-4 border-b-2 border-black pb-4">
          <Search className="h-6 w-6 text-gavra shrink-0" strokeWidth={2.5} />
          <input
            data-testid="products-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by product name or SKU"
            className="w-full bg-transparent font-heading text-xl font-bold uppercase tracking-tight text-black placeholder:text-black/20 outline-none md:text-3xl"
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-5 md:px-10 py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono-hw text-[11px] uppercase tracking-[0.2em] text-black/40">Filter by type</p>
              <div className="mt-4 flex flex-wrap gap-2 lg:flex-col lg:gap-0">
                {allCats.map((c) => (
                  <button
                    key={c}
                    data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setActiveCat(c)}
                    className={`border px-4 py-2 text-left text-[13px] font-bold uppercase tracking-wide transition-colors lg:border-x-0 lg:border-t-0 lg:border-b lg:border-black/10 lg:px-0 lg:py-3 ${
                      activeCat === c
                        ? "border-gavra bg-gavra text-white lg:bg-transparent lg:text-gavra"
                        : "border-black/20 text-black hover:text-gavra"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* grid */}
          <div className="lg:col-span-9">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-mono-hw text-[11px] uppercase tracking-widest text-black/40">
                {loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
              </span>
            </div>
            {!loading && products.length === 0 ? (
              <div data-testid="products-empty" className="border border-black/10 p-16 text-center">
                <p className="font-heading text-2xl font-bold uppercase">No products found</p>
                <p className="mt-2 text-sm text-black/50">Try a different search or filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
